import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  VStack,
  HStack,
  Tag,
  useToast,
  Badge,
} from '@chakra-ui/react';
import { Plus, PencilSimple, Trash } from '@phosphor-icons/react';
import { useArticles } from '../../hooks/useArticles';
import { Article, ArticleMeta, ArticlePreview } from '../../types/article';
import { useTranslation } from 'react-i18next';

const ArticleForm: React.FC<{
  article?: Article;
  onSubmit: (data: Partial<Article>) => void;
  onClose: () => void;
}> = ({ article, onSubmit, onClose }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<Partial<Article>>({
    title: article?.title || '',
    slug: article?.slug || '',
    lead: article?.lead || '',
    content: article?.content || '',
    category: article?.category || 'news',
    year: article?.year || new Date().getFullYear(),
    order: article?.order || 0,
    isPublished: article?.isPublished || false,
    meta: {
      title: article?.meta.title || '',
      description: article?.meta.description || '',
      keywords: article?.meta.keywords || [],
      author: article?.meta.author || '',
      language: article?.meta.language || 'en',
      publishedAt: article?.meta.publishedAt || Date.now(),
    },
  });

  const handleMetaChange = (field: keyof ArticleMeta, value: string | string[]) => {
    setFormData(prev => ({
      ...prev,
      meta: {
        ...prev.meta!,
        [field]: value,
      } as ArticleMeta, // Type assertion to ensure all required fields are present
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit}>
      <VStack spacing={4}>
        <FormControl isRequired>
          <FormLabel>Title</FormLabel>
          <Input
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          />
        </FormControl>

        <FormControl isRequired>
          <FormLabel>Slug</FormLabel>
          <Input
            value={formData.slug}
            onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
            placeholder="url-friendly-slug"
          />
        </FormControl>

        <FormControl isRequired>
          <FormLabel>Lead</FormLabel>
          <Textarea
            value={formData.lead}
            onChange={(e) => setFormData({ ...formData, lead: e.target.value })}
          />
        </FormControl>

        <FormControl isRequired>
          <FormLabel>Content (HTML)</FormLabel>
          <Textarea
            value={formData.content}
            onChange={(e) => setFormData({ ...formData, content: e.target.value })}
            minH="200px"
          />
        </FormControl>

        <HStack width="100%" spacing={4}>
          <FormControl isRequired>
            <FormLabel>Category</FormLabel>
            <Select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
            >
              <option value="news">{t('categories.news')}</option>
              <option value="cube">{t('categories.cube')}</option>
              <option value="tech">{t('categories.tech')}</option>
              <option value="world-of-cubing">{t('categories.world-of-cubing')}</option>
              <option value="training">{t('categories.training')}</option>
            </Select>
          </FormControl>

          <FormControl isRequired>
            <FormLabel>Language</FormLabel>
            <Select
              value={formData.meta?.language}
              onChange={(e) => handleMetaChange('language', e.target.value)}
            >
              <option value="en">English</option>
              <option value="pl">Polish</option>
            </Select>
          </FormControl>
        </HStack>

        <HStack width="100%" spacing={4}>
          <FormControl>
            <FormLabel>Year</FormLabel>
            <Input
              type="number"
              value={formData.year}
              onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
            />
          </FormControl>

          <FormControl>
            <FormLabel>Order</FormLabel>
            <Input
              type="number"
              value={formData.order}
              onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
            />
          </FormControl>
        </HStack>

        <FormControl isRequired>
          <FormLabel>Meta Title</FormLabel>
          <Input
            value={formData.meta?.title}
            onChange={(e) => handleMetaChange('title', e.target.value)}
          />
        </FormControl>

        <FormControl isRequired>
          <FormLabel>Meta Description</FormLabel>
          <Textarea
            value={formData.meta?.description}
            onChange={(e) => handleMetaChange('description', e.target.value)}
          />
        </FormControl>

        <FormControl>
          <FormLabel>Keywords (comma-separated)</FormLabel>
          <Input
            value={formData.meta?.keywords?.join(', ')}
            onChange={(e) => handleMetaChange('keywords', e.target.value.split(',').map(k => k.trim()))}
          />
        </FormControl>

        <FormControl>
          <FormLabel>Author</FormLabel>
          <Input
            value={formData.meta?.author}
            onChange={(e) => handleMetaChange('author', e.target.value)}
          />
        </FormControl>

        <FormControl>
          <FormLabel>Published</FormLabel>
          <Select
            value={formData.isPublished ? 'true' : 'false'}
            onChange={(e) => setFormData({ ...formData, isPublished: e.target.value === 'true' })}
          >
            <option value="true">Yes</option>
            <option value="false">No</option>
          </Select>
        </FormControl>
      </VStack>

      <ModalFooter px={0}>
        <Button variant="ghost" mr={3} onClick={onClose}>
          Cancel
        </Button>
        <Button colorScheme="brand" type="submit">
          {article ? 'Update' : 'Create'} Article
        </Button>
      </ModalFooter>
    </form>
  );
};

const Articles = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [articles, setArticles] = useState<Article[]>([]);
  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null);
  const [loading, setLoading] = useState(true);
  const { getArticlePreviews, createArticle, updateArticle, deleteArticle, getArticle } = useArticles();
  const toast = useToast();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    setLoading(true);
    const articlesByCategory = await getArticlePreviews(i18n.language, true);
    const allArticles = Object.values(articlesByCategory).flat();
    setArticles(allArticles as Article[]);
    setLoading(false);
  };

  const handleAdd = () => {
    setSelectedArticle(null);
    onOpen();
  };

  const handleEdit = async (article: ArticlePreview) => {
    // Fetch the full article data including content
    const fullArticle = await getArticle(article.slug);
    if (fullArticle) {
      setSelectedArticle(fullArticle);
      onOpen();
    } else {
      toast({
        title: 'Failed to load article content',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const handleDelete = async (article: Article) => {
    if (window.confirm('Are you sure you want to delete this article?')) {
      const success = await deleteArticle(article.id);
      if (success) {
        toast({
          title: 'Article deleted successfully',
          status: 'success',
          duration: 3000,
        });
        fetchArticles();
      } else {
        toast({
          title: 'Failed to delete article',
          status: 'error',
          duration: 3000,
        });
      }
    }
  };

  const handleSubmit = async (data: Partial<Article>) => {
    let success;
    if (selectedArticle) {
      success = await updateArticle(selectedArticle.id, data);
    } else {
      success = await createArticle(data as Omit<Article, 'id'>);
    }

    if (success) {
      toast({
        title: `Article ${selectedArticle ? 'updated' : 'created'} successfully`,
        status: 'success',
        duration: 3000,
      });
      onClose();
      fetchArticles();
    } else {
      toast({
        title: `Failed to ${selectedArticle ? 'update' : 'create'} article`,
        status: 'error',
        duration: 3000,
      });
    }
  };

  return (
    <Box>
      <Button
        leftIcon={<Plus />}
        colorScheme="brand"
        mb={6}
        onClick={handleAdd}
      >
        Add Article
      </Button>

      <Table variant="simple">
        <Thead>
          <Tr>
            <Th>Title</Th>
            <Th>Category</Th>
            <Th>Language</Th>
            <Th>Status</Th>
            <Th>Actions</Th>
          </Tr>
        </Thead>
        <Tbody>
          {articles.map((article) => (
            <Tr key={article.id}>
              <Td>{article.title}</Td>
              <Td>
                <Tag colorScheme="brand">
                  {t(`categories.${article.category}`)}
                </Tag>
              </Td>
              <Td>
                <Badge>
                  {article.meta?.language?.toUpperCase() || 'EN'}
                </Badge>
              </Td>
              <Td>
                <Badge
                  colorScheme={article.isPublished ? 'green' : 'yellow'}
                >
                  {article.isPublished ? 'Published' : 'Draft'}
                </Badge>
              </Td>
              <Td>
                <HStack spacing={2}>
                  <IconButton
                    aria-label="Edit article"
                    icon={<PencilSimple />}
                    size="sm"
                    colorScheme="blue"
                    onClick={() => handleEdit(article)}
                  />
                  <IconButton
                    aria-label="Delete article"
                    icon={<Trash />}
                    size="sm"
                    colorScheme="red"
                    onClick={() => handleDelete(article)}
                  />
                </HStack>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>

      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {selectedArticle ? 'Edit Article' : 'Add Article'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <ArticleForm
              article={selectedArticle || undefined}
              onSubmit={handleSubmit}
              onClose={onClose}
            />
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Articles; 